<template>
  <div>
    <validation-observer ref="professionalForm">
      <b-form @submit.prevent="saveProfessionalForm"
              @reset.prevent="clearForm">

        <!-- Situação da habilitação. Só aparece depois de salvo, porque antes
             disso não há o que analisar. -->
        <b-alert :variant="kycVariant" show class="mb-2" v-if="isEdit">
          <div class="alert-body d-flex justify-content-between align-items-center flex-wrap">
            <span>
              <strong>Habilitação:</strong> {{ kycLabel }}
              <template v-if="professional.kyc_notes"> — {{ professional.kyc_notes }}</template>
            </span>
            <b-button
              v-if="canSubmitForReview"
              size="sm"
              variant="outline-primary"
              @click="submitForReview">
              Enviar para análise
            </b-button>
          </div>
        </b-alert>

        <div class="vx-row">
          <div class="vx-col sm:w-1/3 w-full">
            <b-form-group label="CPF" label-for="document">
              <validation-provider
                #default="{ errors }"
                name="CPF"
                rules="required|cpf">
                <b-form-input
                  id="document"
                  v-model="professional.document"
                  :state="errors.length > 0 ? false:null"
                  :disabled="isEdit"
                  name="professional-document"
                  class="required"
                  v-mask="['###.###.###-##']"/>
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-muted d-block" v-if="isEdit">
                  O CPF identifica o cadastro e não pode ser alterado.
                </small>
              </validation-provider>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-2/3 w-full">
            <b-form-group label="Nome completo" label-for="company_name">
              <validation-provider
                #default="{ errors }"
                name="Nome completo"
                rules="required">
                <b-form-input
                  id="company_name"
                  v-model="professional.company_name"
                  :state="errors.length > 0 ? false:null"
                  name="company_name"
                  class="required"/>
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-muted d-block">Nome civil, como no documento. É o que vai ao gateway de pagamento.</small>
              </validation-provider>
            </b-form-group>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group label="Nome público" label-for="trading_name">
              <validation-provider
                #default="{ errors }"
                name="Nome público"
                rules="required">
                <b-form-input
                  id="trading_name"
                  v-model="professional.trading_name"
                  :state="errors.length > 0 ? false:null"
                  name="trading_name"
                  class="required"/>
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-muted d-block">Como o profissional aparece na busca e na página dele.</small>
              </validation-provider>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <!--
              Obrigatória e com idade mínima: o backend passou a exigir a data para
              criar conta (LGPD, Art. 14), e sem o campo preenchido o cadastro do
              profissional falharia com 422 sem explicação na tela.
            -->
            <validation-provider
              #default="validationContext"
              name="Data de nascimento"
              rules="required|min_age"
              slim>
              <b-form-group label="Data de nascimento" label-for="birthdate">
                <b-form-input
                  id="birthdate"
                  v-model="professional.birthdate"
                  type="date"
                  class="required"
                  name="birthdate"/>
                <b-form-invalid-feedback :state="validationContext.errors.length === 0">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>
        </div>

        <!-- Dados da conta. Só no cadastro: depois disso o usuário já existe e a
             edição de e-mail/nome passa pela tela de usuários. -->
        <div class="vx-row" v-if="!isEdit">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group label="E-mail de acesso" label-for="email">
              <validation-provider
                #default="{ errors }"
                name="E-mail"
                rules="required|email">
                <b-form-input
                  id="email"
                  v-model="professional.email"
                  :state="errors.length > 0 ? false:null"
                  name="email"
                  class="required"/>
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-muted d-block">
                  Se o CPF ou o e-mail já existirem, a conta é reaproveitada.
                </small>
              </validation-provider>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group label="Telefone" label-for="phone">
              <b-form-input
                id="phone"
                v-model="professional.phone"
                name="phone"
                v-mask="['(##) ####-####', '(##) #####-####']"/>
            </b-form-group>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <b-form-group label="Formas de atendimento">
              <b-form-checkbox v-model="professional.serves_in_person" class="mb-1">
                No espaço do profissional
              </b-form-checkbox>
              <b-form-checkbox v-model="professional.serves_at_home" class="mb-1">
                No endereço do aluno (domicílio)
              </b-form-checkbox>
              <b-form-checkbox v-model="professional.serves_online">
                Online
              </b-form-checkbox>
              <small class="text-muted d-block mt-1">
                Quem atende online aparece em qualquer busca por proximidade.
              </small>
            </b-form-group>
          </div>
          <!-- Mudar o status é liberar (ou barrar) alguém para vender. Fica só para
               quem tem legal_entities.approve — o backend recusa de todo modo. -->
          <div class="vx-col sm:w-1/2 w-full" v-if="canApprove">
            <b-form-group :label="$t('legal_entity.status')" label-for="status">
              <b-form-radio-group
                id="status"
                v-model="professional.status"
                name="status"
                :options="statusOptions"/>
            </b-form-group>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full">
            <b-form-group
              id="description"
              label="Sobre o profissional"
              label-for="description">
              <!-- auto-open: sem isto o campo (obrigatório na habilitação) renderiza um
                   placeholder indistinguível de texto estático — precisa de um clique
                   "escondido" para virar editor, e sem esse clique salva vazio. -->
              <complex-editor :editor_data.sync="professional.description" :auto-open="true"/>
            </b-form-group>
          </div>
        </div>

        <b-form-row class="justify-center" v-if="isEdit">
          <b-col cols="auto">
            <label class="w-full text-left block">Foto de perfil</label>
            <image-upload
              class="border-faint-grey border-solid border p-2 rounded-lg"
              maxHeight="140px"
              maxWidth="140px"
              hint="Tamanho recomendado: 800×1000px (retrato, proporção 4:5), rosto centralizado e próximo ao topo — a foto é cortada verticalmente nos cards de busca e no perfil público."
              v-model="imageData"
              :key="logoUploadURL"
              :uploadUrl="logoUploadURL"
              @upload-complete="uploadLogoSuccess"/>
          </b-col>
        </b-form-row>

        <div class="vx-row mt-2 mb-2">
          <div class="vx-col w-full flex gap-2 justify-end">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="float-right"
              :disabled="!validateForm"
              type="submit">
              Salvar
            </b-button>
            <b-button type="reset" variant="outline-secondary" class="float-right">
              Limpar
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-secondary"
              @click="goBack"
              class="mr-2 float-right">
              Voltar
            </b-button>
          </div>
        </div>

      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BForm, BFormRow, BCol, BButton, BFormInput, BFormGroup, BFormRadioGroup, BFormCheckbox, BAlert
} from 'bootstrap-vue'
// Importar @validations registra as regras globais do vee-validate — inclusive
// `cpf`, que valida o dígito verificador e já existia sem nenhum formulário usando.
import { required, email } from '@validations'
import ComplexEditor from '@/components/ComplexEditor.vue'
import ImageUpload from '@/components/ImageUpload.vue'

import ProfessionalService from '@/services/api/ProfessionalService'

export default {
  components: {
    BForm,
    BFormRow,
    BCol,
    BButton,
    BFormInput,
    BFormGroup,
    BFormRadioGroup,
    BFormCheckbox,
    BAlert,
    ValidationProvider,
    ValidationObserver,
    ComplexEditor,
    ImageUpload
  },
  props: {
    legalEntityId: {
      type: Number,
      default: null
    },
    professional: {
      type: Object,
      default: () => ({})
    },
    services: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    professionalService: null,
    statusOptions: [
      { text: 'Em cadastro', value: 'IR' },
      { text: 'Ativo', value: 'AC' },
      { text: 'Inativo', value: 'IN' },
      { text: 'Bloqueado', value: 'BL' },
    ],
    kycLabels: {
      PE: 'Pendente de análise',
      AP: 'Aprovado',
      RJ: 'Recusado',
    },
  }),
  computed: {
    isEdit() {
      return this.legalEntityId != null
    },
    canApprove() {
      return this.$acl.hasPermission('legal_entities.approve')
    },
    canSubmitForReview() {
      return this.isEdit && this.professional.status !== 'AC'
    },
    kycLabel() {
      return this.kycLabels[this.professional.kyc_status] || 'Pendente de análise'
    },
    kycVariant() {
      if (this.professional.kyc_status === 'AP') return 'success'
      if (this.professional.kyc_status === 'RJ') return 'danger'
      return 'warning'
    },
    validateForm() {
      const base = !this.isEmpty(this.professional.document) &&
        !this.isEmpty(this.professional.trading_name) &&
        !this.isEmpty(this.professional.company_name)

      return this.isEdit ? base : (base && !this.isEmpty(this.professional.email))
    },
    logoUploadURL() {
      const base = `${process.env.VUE_APP_API_BASE_URL}/api/v1`
      return `${base}/legal_entities/${this.legalEntityId}/logo`
    },
    imageData() {
      if (this.isEdit) {
        return this._.get(this.professional, 'logo_url')
      }
    }
  },
  methods: {
    saveProfessionalForm() {
      this.$refs.professionalForm.validate().then(success => {
        if (!success) return

        this.clearErrors(this.$refs.professionalForm)
        this.$vs.loading()

        const request = this.isEdit ? this.updateProfessional() : this.registerProfessional()

        request.then(
          data => {
            this.$vs.loading.close()
            this.notifySuccess(this.$vs)
            this.$emit('update-data', data)
          },
          error => {
            this.$vs.loading.close()
            this.showErrors(this.$refs.professionalForm, error)
          }
        )
      })
    },
    /**
     * Cadastro novo passa pelo endpoint guiado: ele cria a conta de usuário, a
     * pessoa com o CPF, o fornecedor e — o que importa — o vínculo de responsável,
     * que é o que dá ao profissional acesso ao próprio cadastro.
     */
    registerProfessional() {
      const params = {
        document: this.clearDashDots(this.professional.document),
        name: this.professional.company_name,
        email: this.professional.email,
        phone: this.professional.phone,
        birthdate: this.professional.birthdate || null,
        trading_name: this.professional.trading_name,
        description: this.professional.description,
        serves_in_person: !!this.professional.serves_in_person,
        serves_at_home: !!this.professional.serves_at_home,
        serves_online: !!this.professional.serves_online,
        service_radius_km: this.professional.service_radius_km || null,
      }

      // Mesma guarda de updateProfessional(): o backend também descarta `status`
      // de quem não pode aprovar, mas só enviar quando faz sentido deixa a
      // intenção explícita em vez de depender só dessa proteção.
      if (this.canApprove) {
        params.status = this.professional.status
      }

      return this.professionalService.register(params)
    },
    updateProfessional() {
      const params = {
        id: this.professional.id,
        person_type: 'NE',
        document: this.clearDashDots(this.professional.document),
        trading_name: this.professional.trading_name,
        company_name: this.professional.company_name,
        description: this.professional.description,
        serves_in_person: !!this.professional.serves_in_person,
        serves_at_home: !!this.professional.serves_at_home,
        serves_online: !!this.professional.serves_online,
        service_radius_km: this.professional.service_radius_km || null,
      }

      // O backend descarta `status` de quem não pode aprovar; não enviar deixa a
      // intenção explícita em vez de depender só dessa proteção.
      if (this.canApprove) {
        params.status = this.professional.status
      }

      return this.services.legalEntityService.createOrUpdate(params)
    },
    submitForReview() {
      this.$vs.loading()
      this.services.legalEntityService.submitProfessionalForReview(this.professional.id).then(
        data => {
          this.$vs.loading.close()
          this.notifySuccess(this.$vs, 'Cadastro enviado para análise.')
          this.$emit('update-data', data)
        },
        error => {
          this.$vs.loading.close()
          this.showErrors(this.$refs.professionalForm, error)
        }
      )
    },
    clearForm() {
      this.$emit('reset')
      this.professional.document = ''
      this.professional.company_name = ''
      this.professional.trading_name = ''
      this.professional.description = ''
      this.professional.email = ''
    },
    goBack() {
      this.$router.push('/professionals')
    },
    uploadLogoSuccess() {
      this.notifySuccess(this.$vs, this.$t('messages.success'), 5000)
    },
  },
  beforeMount() {
    this.professionalService = ProfessionalService.build(this.$vs)
  }
}
</script>

<style lang="scss" scoped>
</style>
